import { useState, useEffect } from 'react';
import { IEpisode, CreateEpisodeRequest, UpdateEpisodeRequest, PaginationResult, EpisodeQuery, PaginationQuery, EpisodeStatus } from '@packages/global';
import { episodeApi } from '../services/user/episode.api';
import { useDialog } from './useDialog';

export interface EpisodeFormData {
  title: string;
  description: string;
  episodeNumber: number;
  status: EpisodeStatus;
  projectId?: string;
}

export function useEpisodesList(projectId: string) {
  const [episodes, setEpisodes] = useState<IEpisode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState<IEpisode | null>(null);
  const [formData, setFormData] = useState<EpisodeFormData>({
    title: '',
    description: '',
    episodeNumber: 1,
    status: EpisodeStatus.DRAFT
  });
  const [searchTerm, setSearchTerm] = useState('');
  const { confirm } = useDialog();
  const [pagination, setPagination] = useState<PaginationResult>({
    page: 1,
    limit: 48,
    total: 0,
    totalPages: 0,
    sortBy: 'episodeNumber',
    sortOrder: 'asc'
  });

  useEffect(() => {
    loadEpisodes({ projectId, search: searchTerm }, pagination.page, pagination.limit);
  }, [searchTerm, pagination.page, projectId]);

  const loadEpisodes = async (
    queryParams: Partial<EpisodeQuery> = {},
    page: number = 1,
    limit: number = 48
  ) => {
    try {
      const queryData: PaginationQuery<EpisodeQuery> = {
        query: {
          search: queryParams.search ?? searchTerm,
          ...queryParams
        },
        pagination: {
          page,
          limit,
          sortBy: 'episodeNumber',
          sortOrder: 'asc'
        }
      };
      const response = await episodeApi.episodePageList(queryData);
      setEpisodes(response.items);
      setPagination((prev) => ({
        ...prev,
        page,
        limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));
    } catch (error) {
      console.error('加载短剧失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingEpisode) {
        const updateData: UpdateEpisodeRequest = formData;
        await episodeApi.updateEpisode(editingEpisode.id, updateData);
      } else {
        const createData: CreateEpisodeRequest = formData;
        await episodeApi.createEpisode(createData);
      }
      resetForm();
      loadEpisodes({ projectId, search: searchTerm }, pagination.page, pagination.limit);
    } catch (error) {
      console.error('保存短剧失败:', error);
    }
  };

  const handleEdit = (episode: IEpisode) => {
    setEditingEpisode(episode);
    setFormData({
      title: episode.title,
      description: episode.description || '',
      episodeNumber: episode.episodeNumber || 1,
      status: episode.status,
      projectId: episode.projectId || undefined
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm('确定要删除这个短剧吗？', '确认删除', 'danger');
    if (confirmed) {
      try {
        await episodeApi.deleteEpisode(id);
        loadEpisodes({ projectId, search: searchTerm }, pagination.page, pagination.limit);
      } catch (error) {
        console.error('删除短剧失败:', error);
      }
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (!value) {
      loadEpisodes({ projectId }, 1, pagination.limit);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    loadEpisodes({ projectId }, 1, pagination.limit);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingEpisode(null);
    setFormData({
      title: '',
      description: '',
      episodeNumber: 1,
      status: EpisodeStatus.DRAFT
    });
  };

  const handlePageChange = (page: number) => {
    loadEpisodes({ projectId, search: searchTerm }, page, pagination.limit);
  };

  return {
    episodes,
    loading,
    showForm,
    editingEpisode,
    formData,
    searchTerm,
    pagination,
    setFormData,
    setShowForm,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleSearchChange,
    handleClearSearch,
    resetForm,
    handlePageChange,
    loadEpisodes
  };
}